import { Button, Descriptions, theme, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CustomerDetailResponse, CustomerContactResponse, CustomerLocationResponse } from '@/types/api/customer';
import type { CountryInfo, StateInfo, CityInfo } from '@/types/api/location';
import { controlHeightTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import { formatPhoneFromApiForDisplay } from '@/utils/phoneUtils';
import { formatAddressWithResolvedNames } from '@/utils/locationUtils';
import { AddressParseFailedTag } from '@/components/common/AddressParseFailedTag';
import ExpandableText from '@/components/common/ExpandableText';
import ContactsSection from './ContactsSection';
import LocationsSection from './LocationsSection';

const { Text } = Typography;

interface PrimaryInfoSectionProps {
  customer: CustomerDetailResponse;
  parentCompanyName: string | null;
  countries: CountryInfo[];
  states: StateInfo[];
  cities: CityInfo[];
  canEdit: boolean;
  onEditBasicInfo: () => void;
  // Contacts props (#91 — view-only; rows/creation link to the Contacts module)
  contacts: CustomerContactResponse[] | undefined;
  includeInactiveContacts: boolean;
  onIncludeInactiveContactsChange: (checked: boolean) => void;
  onOpenContact: (contact: CustomerContactResponse) => void;
  onAddInContactsModule: () => void;
  contactsLoading: boolean;
  // Locations props
  locations: CustomerLocationResponse[] | undefined;
  includeInactiveLocations: boolean;
  onIncludeInactiveLocationsChange: (checked: boolean) => void;
  onAddLocation: () => void;
  onEditLocation: (location: CustomerLocationResponse) => void;
  onViewLocation: (location: CustomerLocationResponse) => void;
  onSetDefaultShipping: (location: CustomerLocationResponse) => void;
  onSetDefaultBilling: (location: CustomerLocationResponse) => void;
  onToggleLocationStatus: (location: CustomerLocationResponse) => void;
  locationsLoading: boolean;
  canAdd: boolean;
  getItemPermissions: (item: CustomerContactResponse | CustomerLocationResponse) => {
    canEdit: boolean;
    canSetPrimary: boolean;
    canSetDefault: boolean;
    canSetDefaultShipping: boolean;
    canSetDefaultBilling: boolean;
    canToggleStatus: boolean;
    canDelete: boolean;
    isItemActive: boolean;
    isProtectedFromDeactivate: boolean;
  };
}

const PrimaryInfoSection = ({
  customer,
  parentCompanyName,
  countries,
  states,
  cities,
  canEdit,
  onEditBasicInfo,
  contacts,
  includeInactiveContacts,
  onIncludeInactiveContactsChange,
  onOpenContact,
  onAddInContactsModule,
  contactsLoading,
  locations,
  includeInactiveLocations,
  onIncludeInactiveLocationsChange,
  onAddLocation,
  onEditLocation,
  onViewLocation,
  onSetDefaultShipping,
  onSetDefaultBilling,
  onToggleLocationStatus,
  locationsLoading,
  canAdd,
  getItemPermissions,
}: PrimaryInfoSectionProps) => {
  const { t } = useTranslation('customers');
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const elevation = useElevation();

  return (
    <div>
      {/* Basic Information */}
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h3
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
            margin: 0,
          }}
        >
          {t('customer.sections.basicInformation')}
        </h3>
        <Button
          type="primary"
          icon={<EditOutlined />}
          disabled={!canEdit}
          onClick={onEditBasicInfo}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        >
          {t('customer.actions.edit')}
        </Button>
      </div>
      <div
        style={{
          backgroundColor: token.colorBgContainer,
          overflow: 'hidden',
          marginBottom: '24px',
          ...elevation.card,
        }}
      >
        <Descriptions bordered column={2} size="middle">
          <Descriptions.Item label={t('customer.fields.salesRep')}>
            {customer.internalContact?.fullName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('customer.fields.parentCompany')}>
            {parentCompanyName ? (
              <a
                onClick={() => navigate(`/auth/customers/${customer.parentCompany?.companyId || (customer as unknown as { parentCompanyId?: number })?.parentCompanyId}`)}
                style={{ color: token.colorPrimary, cursor: 'pointer', textDecoration: 'none' }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                {parentCompanyName}
              </a>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('customer.fields.email')}>{customer.email || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('customer.fields.website')}>{customer.web || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('customer.fields.phone')}>
            {formatPhoneFromApiForDisplay(
              {
                phoneCountryCode: customer.phoneCountryCode,
                phoneNumber: customer.phoneNumber,
                phoneExtension: customer.phoneExtension,
              },
              t('customer.fields.ext')
            ) || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('customer.fields.fax')}>{customer.fax || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('customer.fields.registeredAddress')} span={2}>
            {formatAddressWithResolvedNames(
              {
                address: customer.address,
                cityId: customer.cityId,
                stateId: customer.stateId,
                postcode: customer.postcode,
                countryId: customer.countryId,
              },
              countries,
              states,
              cities
            )}
            {/* #101 BE-03: unparseable address → visible warning, not a silent dash */}
            {customer.parseStatus === 'failed' && <AddressParseFailedTag />}
          </Descriptions.Item>
          <Descriptions.Item label={t('customer.sections.notes')} span={2}>
            <div className="tw-grid tw-grid-cols-3 tw-gap-6">
              <div>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: '4px' }}>
                  {t('customer.fields.account')}
                </Text>
                <ExpandableText text={customer.accountNotes} maxLength={40} />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: '4px' }}>
                  {t('customer.fields.delivery')}
                </Text>
                <ExpandableText text={customer.deliveryNotes} maxLength={40} />
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: '4px' }}>
                  {t('customer.fields.sales')}
                </Text>
                <ExpandableText text={customer.salesNotes} maxLength={40} />
              </div>
            </div>
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* Contact Table — #91 view-only, links to the Contacts module */}
      <div className="tw-mt-8">
        <ContactsSection
          contacts={contacts}
          includeInactive={includeInactiveContacts}
          onIncludeInactiveChange={onIncludeInactiveContactsChange}
          onOpenContact={onOpenContact}
          onAddInContactsModule={onAddInContactsModule}
          loading={contactsLoading}
        />
      </div>

      {/* Location Table */}
      <div className="tw-mt-8">
        <LocationsSection
          locations={locations}
          includeInactive={includeInactiveLocations}
          onIncludeInactiveChange={onIncludeInactiveLocationsChange}
          onAddLocation={onAddLocation}
          onEditLocation={onEditLocation}
          onViewLocation={onViewLocation}
          onSetDefaultShipping={onSetDefaultShipping}
          onSetDefaultBilling={onSetDefaultBilling}
          onToggleStatus={onToggleLocationStatus}
          loading={locationsLoading}
          canAdd={canAdd}
          getItemPermissions={getItemPermissions as (item: CustomerLocationResponse) => { canEdit: boolean; canSetPrimary: boolean; canSetDefault: boolean; canSetDefaultShipping: boolean; canSetDefaultBilling: boolean; canToggleStatus: boolean; canDelete: boolean; isItemActive: boolean; isProtectedFromDeactivate: boolean }}
        />
      </div>
    </div>
  );
};

export default PrimaryInfoSection;
